import { AdType, DailyBudgetPlan, DeliverType, RecommendBidPrice, RtbCampaignPlanType, RtbOptimize } from 'core/rtbCampaign/RtbCampaign';
import { DefaultRtbCampaignBasicFormModel, RtbCampaignBasicFormModel, RtbCampaignBasicFormModelConstructorParams } from './RtbCampaignBasicFormModel';
import { OPERATE } from 'enum/Operate';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { omitBy, isEmpty, defaultTo, uniq } from 'lodash';
import { computeRecommendBidPrice } from 'core/pchome/RecommendBidPriceCalculator';
import { DEFAULT_KEYWORD_BID_CAP, DEFAULT_KEYWORD_BID_FLOOR } from 'core/pchome/KeywordBidCompetitiveness';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import i18n from 'i18n';
import { getLimitationValueContent } from 'utils/LimitationUtil';
import { AdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { MAX_TOTAL_KEYWORDS, SuggestKeywords, createUnresolvedKeywordRow } from 'core/product/Product';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { SelectOptions } from 'components/commonType';

export interface KeywordFormModel extends RtbCampaignBasicFormModel {
  readonly canEditBudgetPlan: boolean;
  readonly canEditPriceModel: boolean;
}
export abstract class AbstractKeywordFormModel extends DefaultRtbCampaignBasicFormModel implements KeywordFormModel {

  keywords: string[];
  keywordsSuggestThreshold: number = 20;
  // 已知的關鍵字成效列（key = 關鍵字）。兩個餵入點都是 /pchome/recommendKeywords 的回應，
  // 而且兩邊都要把 recommendPool 與 enrichedSelected 一起記下來：
  // `優化目標` 頁的已選清單，以及 processAlertStep 為了湊滿 20 個字而撈的關鍵字建議書。
  // map 只增不減，所以查無此 key 代表「從來沒問到過」，不代表「這個字沒有成效」。
  private keywordRowMap: Map<string, SuggestKeywords> = new Map();

  constructor (
    mode: 'create' | 'edit',
    private adRequestSourceManager: AdRequestSourceManager,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(mode, ...args);
    this.defaultDeliverType = DeliverType.STANDARD;
    this.keywords = [];
  }

  get campaignAdType (): AdType {
    return AdType.KEYWORD;
  }

  get needSetupAgeGender (): boolean {
    return false;
  }

  get showDeliverType (): boolean {
    return +this.modelDailyBudgetType === DailyBudgetPlan.DAILY;
  }

  get showTags (): boolean {
    return false;
  }

  get showOptimizeSection (): boolean {
    return false;
  }

  get limitationOperates (): {
    need: string[],
    notNeed?: string[],
    other?: string[]
  } {
    return {
      need: [OPERATE.INCLUDE],
      notNeed: undefined,
      other: undefined
    };
  }

  /**
   * 記下 /pchome/recommendKeywords 回來的成效列。兩個餵入點（與 keywordRowMap 的欄位註解
   * 一致）：`優化目標` 頁的已選清單 —— 主要時機是元件 mount，之後已選關鍵字每次變動也會
   * 再餵一次；以及 processAlertStep 為了湊滿 20 個字而撈的關鍵字建議書（見
   * getKeywordProposalsOptions），那條與已選關鍵字的異動無關。
   *
   * 兩者都刻意不 fireEvent，但理由不同：前者的重算走既有的 onEnter →
   * fetchRecommendBidPrice，在 LIMITATION tab 每改一次就 fireEvent 只會多出沒人在看的
   * render；後者是 goNext 流程的一環，而「進入 `基本資訊` tab」那條把 goNext 綁成
   * fetchRecommendBidPrice（見 RtbCampaignSetupStep.handleOnEnterBasicTab）⇒ 重算本來
   * 就排在這張 map 填完之後。
   */
  rememberKeywordRows (rows: SuggestKeywords[]): void {
    rows.forEach(row => this.keywordRowMap.set(row.suggestKeyword, row));
  }

  /**
   * `基本資訊` 的「出價效益參考」卡片要列的就是這批列。以 `this.keywords`（= 最近一次
   * fetchRecommendBidPrice 讀到的 limitation 內容）為準，而不是 keywordRowMap 的全部 ——
   * map 是累積的，裡面還有關鍵字建議書撈進來、但使用者沒選的字。
   *
   * 反過來，`this.keywords` 有、map 沒有的字會補一列佔位列（見 resolveKeywordRows）：
   * 卡片列的是「使用者選了哪些字」，缺成效是那一列顯示不出數字，不是那個字不存在。
   */
  get selectedKeywordRows (): SuggestKeywords[] {
    return this.resolveKeywordRows(this.keywords);
  }

  /**
   * `優化目標` 頁在 remount 後用它把已選清單 hydrate 回來,別再從 limitation 重建成佔位列。
   *
   * 沒有它的話,同一個關鍵字會在兩頁顯示不同狀態:卡片讀這張累積式的 map(跨 remount 存活),
   * 面板讀的 `selectedList` 則是每次 remount 都從 limitation 重建(limitation 不存成效)⇒
   * 只要 remount 後那次 `/recommendKeywords` 沒回來，面板就退回全 0 的佔位列、區間顯示成
   * 預設的 8–20，而卡片還記得真實區間 ⇒ 兩頁講的是同一個字卻各說一套。使用者會以為系統
   * 自相矛盾 —— 而它們只是新舊程度不同。
   *
   * 這是讀的方向,與「只往上報 API 回來的列」那條寫入規則不衝突:
   * map 裡只會有伺服器回來的列,拿它 hydrate 不會把空殼洗進任何地方。
   */
  recallKeywordRow (keyword: string): SuggestKeywords | undefined {
    return this.keywordRowMap.get(keyword);
  }

  /**
   * map 裡沒有的字要補一列尚未解析的佔位列,不可以靜默丟掉。
   *
   * 丟掉的版本會讓使用者選過的關鍵字在「出價效益參考」卡片裡憑空消失 —— 而且是「安靜地」消失：
   * 全部都查無此列時卡片會整個空掉，配上空狀態文案「尚未選擇關鍵字」，畫面直接說謊。
   * 實際踩到的情境是手輸入的字：它不會出現在 recommendPool 裡（推薦池不含使用者自己打的字），
   * 唯一的來源是 `/recommendKeywords` 的 `enrichedSelected`，那一次往返只要沒回來
   * （beta 上 PChome API 逾時就會 504，切 tab 也會經由 useCallAPI 的 unmount cleanup 取消它），
   * 這個 key 就永遠不會進 map。
   *
   * 佔位列的 `cpcCounts` 為 0 ⇒ 顯示端會換成預設區間 8–20（見 `resolveKeywordBidRange`），
   * 而 `computeRecommendBidPrice` 也會因為同一個欄位把它濾掉 ⇒ 活動層級的建議出價區間不受影響。
   */
  private resolveKeywordRows (keywords: string[]): SuggestKeywords[] {
    return keywords.map(keyword =>
      defaultTo(this.keywordRowMap.get(keyword), createUnresolvedKeywordRow(keyword, []))
    );
  }

  /**
   * 建議出價區間改由前端自算（見 core/pchome/RecommendBidPriceCalculator），
   * 不再呼叫 POST /pchome/suggestedKeywordsCpcBidRange —— 使用者在「出價試算」面板
   * 反覆移除／還原關鍵字時，區間必須與同一畫面上同步 render 的計數、覆蓋率一起變，
   * 中間夾一支 API 會讓同一塊 UI 出現兩種速度。
   *
   * 也刻意不保留原本「關鍵字集合沒變就跳過」的短路：那是為了省一次 HTTP 才存在的，
   * 換成純函式後它只剩下風險 —— 首次進 `基本資訊` 若早於 /recommendKeywords 回填，
   * 集合相同但成效列還是空殼，短路會把 8/20 fallback 鎖住不再重算。
   */
  override async fetchRecommendBidPrice (limitationValue: LimitationData): Promise<void> {
    const searchKeywords = uniq([
      ...getLimitationValueContent(limitationValue, OPERATE.INCLUDE, 'searchKeywords'),
      ...getLimitationValueContent(limitationValue, OPERATE.INCLUDE, 'recommendedKeywords')
    ]);
    this.recommendBidPrice = computeRecommendBidPrice(this.resolveKeywordRows(searchKeywords));
    this.keywords = searchKeywords;
    this.updateState(false);
  }

  async getKeywordProposalsOptions (inputKeywords: string[], recommendedKeywords: string[]): Promise<SelectOptions[]> {
    const originalKeywords = new Set([...inputKeywords, ...recommendedKeywords]);
    let pool: SuggestKeywords[] = [];
    try {
      const response = await this.adRequestSourceManager.getRecommendKeywords({
        goodsCodes: [],
        selectedKeywords: [...originalKeywords]
      });
      pool = response.recommendPool;
      // 兩桶都要記。只記 pool 的話，這條路徑永遠學不到「使用者自己打的字」——
      // 手輸入的關鍵字依定義不在推薦池裡，它的成效列只會出現在 enrichedSelected。
      // 順序與 `優化目標` 頁的那個呼叫點一致：enrichedSelected 後寫，同名時它是比較準的那一份。
      this.rememberKeywordRows([...pool, ...response.enrichedSelected]);
    } catch (e) {
      console.error('Error fetching recommend keywords:', e);
    }
    return pool
      .filter(row => !originalKeywords.has(row.suggestKeyword))
      .slice(0, MAX_TOTAL_KEYWORDS - originalKeywords.size)
      .map(row => ({
        label: row.suggestKeyword,
        value: row.suggestKeyword,
        extra: { sources: row.sources }
      }));
  }

  override async processAlertStep (limitationModel: EditLimitationModel, goNext: () => void, goLast: () => void): Promise<void> {
    const limitationValue: LimitationData = limitationModel.limitationValue;
    const inputKeywordOptions: SelectOptions[] = (() => {
      const entry = limitationValue.include?.find((l: Limitation) => l.type === 'searchKeywords');
      return entry && Array.isArray(entry.value) ? entry.value as SelectOptions[] : [];
    })();
    const inputKeywords = getLimitationValueContent(limitationValue, OPERATE.INCLUDE, 'searchKeywords');
    const recommendedKeywords = getLimitationValueContent(limitationValue, OPERATE.INCLUDE, 'recommendedKeywords');
    const keywords = uniq([
      ...inputKeywords,
      ...recommendedKeywords
    ]);

    if (keywords.length >= this.keywordsSuggestThreshold) {
      goNext();
      return;
    }

    this.updateState(true);

    let keywordProposalOptions: SelectOptions[] =
      await this.getKeywordProposalsOptions(inputKeywords, recommendedKeywords);

    const hideModalFunc: () => void = () => {
      this.alertModalData = undefined;
      this.updateState(false);
    };
    const confirmFunc: () => void = () => {
      hideModalFunc();
      goNext();
    };

    if (keywordProposalOptions.length === 0) {
      const retreatFunc: () => void = () => {
        hideModalFunc();
        goLast();
      };

      this.alertModalData = {
        title: i18n.t<string>('common.titles.hint'),
        message: i18n.t<string>('campaign.messages.searchKeywordsAlertWithoutKeyworProposals', { threshold: this.keywordsSuggestThreshold }),
        dismiss: retreatFunc,
        onCancel: retreatFunc,
        onConfirm: confirmFunc,
        primaryTitle: i18n.t<string>('campaignSetupFlow.labels.nextStep'),
        secondaryTitle: i18n.t<string>('campaign.labels.backToKeywordSetting')
      };
    } else {
      const retreatFunc: () => void = () => {
        const newKeywordLimits = [
          ...inputKeywordOptions,
          ...keywordProposalOptions
        ];
        limitationModel.setLimitation(OPERATE.INCLUDE, 'searchKeywords', newKeywordLimits);
        hideModalFunc();
        goNext();
      };

      this.alertModalData = {
        title: i18n.t<string>('common.titles.hint'),
        message: i18n.t<string>('campaign.messages.searchKeywordsAlertWithKeyworProposals', { threshold: this.keywordsSuggestThreshold }),
        dismiss: retreatFunc,
        onCancel: retreatFunc,
        onConfirm: confirmFunc,
        primaryTitle: i18n.t<string>('campaignSetupFlow.labels.nextStep'),
        secondaryTitle: i18n.t<string>('campaign.labels.addKeyword')
      };
    }

    this.updateState(false);
  }

  getBidPriceRange (optimize: RtbOptimize): {
    max?: number,
    min: number,
    recommend?: {
      min: number,
      max: number
    }
  } {
    const bidFloor = this.getBidFloor(optimize);
    return {
      max: this.getBidCap(optimize),
      min: bidFloor,
      recommend: this.clampRecommendBidPrice(this.state.recommendBidPrice, bidFloor)
    };
  }

  /**
   * 「出價試算」面板的區間要跟著尚未寫回的 draft 走，所以不能讀 state.recommendBidPrice；
   * 但 clamp 與 8/20 fallback 必須與 `基本資訊` 共用同一份 —— 兩頁顯示同一個數字是
   * 由構造保證的，不是靠兩邊各算一次剛好算得一樣。
   */
  getRecommendBidPriceRangeOf (rows: SuggestKeywords[], optimize: RtbOptimize): {
    min: number,
    max: number
  } {
    return this.clampRecommendBidPrice(computeRecommendBidPrice(rows), this.getBidFloor(optimize));
  }

  private clampRecommendBidPrice (
    recommendBidPrice: RecommendBidPrice | undefined,
    bidFloor: number
  ): { min: number, max: number } {
    // 8 / 20 原本在這裡硬編碼兩次，現在與 per-keyword 的預設區間共用同一份常數 ——
    // 兩者語意本來就相同（BE 也是同一對 DEFAULT_KEYWORD_BID_*_PRICE）。
    return !isEmpty(recommendBidPrice) ? {
      min: Math.max(defaultTo(recommendBidPrice.bidFloor, DEFAULT_KEYWORD_BID_FLOOR), bidFloor),
      max: Math.max(defaultTo(recommendBidPrice.bidCap, DEFAULT_KEYWORD_BID_CAP), bidFloor)
    } : {
      min: Math.max(DEFAULT_KEYWORD_BID_FLOOR, bidFloor),
      max: Math.max(DEFAULT_KEYWORD_BID_CAP, bidFloor)
    };
  }

  getRtbOptionsMap () {
    const optionsMap = super.getRtbOptionsMap();
    const enableFCPC = this.addonFeatureManager.isFeatureEnable(ADDONFEATURE.CAMPAIGN.FIXED_CPC);
    return omitBy({
      ...optionsMap,
      [L1ObjectObjective.TRAFFIC]: {
        [RtbCampaignPlanType.RS_CPC]: [
          L2ObjectOptimizationGoal.CLICKS
        ],
        [RtbCampaignPlanType.FCPC]: enableFCPC ? [
          L2ObjectOptimizationGoal.CLICKS
        ] : undefined
      }
    }, isEmpty);
  }

  setupDefaultCampaign (campaign: any) {
    super.setupDefaultCampaign(campaign);
    campaign.dailyTargetBudget = 0;
  }
}

export class EditKeywordFormModel extends AbstractKeywordFormModel {
  constructor (adRequestSourceManager: AdRequestSourceManager, ...args: RtbCampaignBasicFormModelConstructorParams) {
    super('edit', adRequestSourceManager, ...args);
  }
  get canEditBudgetPlan (): boolean {
    return false;
  }
  get canEditPriceModel (): boolean {
    return false;
  }
}

export class CreateKeywordFormModel extends AbstractKeywordFormModel {
  constructor (adRequestSourceManager: AdRequestSourceManager, ...args: RtbCampaignBasicFormModelConstructorParams) {
    super('create', adRequestSourceManager, ...args);
    this.modelDailyBudgetType = DailyBudgetPlan.DAILY;
  }
  get canEditBudgetPlan (): boolean {
    return true;
  }
  get canEditPriceModel (): boolean {
    return true;
  }
}
